/**
 * appliquerLangueAuDocument — reporte la langue choisie sur l'élément <html>.
 *
 * Trois attributs, trois raisons :
 *
 *   `lang`  : les lecteurs d'écran changent de voix et de règles de coupure ;
 *             sans lui, une interface arabe est lue avec une prosodie française.
 *   `dir`   : bascule toute la mise en page en RTL. C'est le seul commutateur —
 *             les feuilles de style sont écrites en propriétés logiques
 *             (`margin-inline-start` plutôt que `margin-left`), qui suivent
 *             `dir` toutes seules.
 *   `class` : `notranslate` RESTE, quelle que soit la langue. Il n'est pas là
 *             pour empêcher les autres langues, mais parce que la traduction
 *             automatique de Chrome réécrit les nœuds de texte sous React, qui
 *             tente ensuite de les retirer et lève. Maintenant que l'application
 *             se traduit elle-même, l'auto-traduction n'a plus rien à apporter
 *             et garde tous ses inconvénients.
 */

import { directionDe, localeIntlDe, LANGUE_PAR_DEFAUT } from './languesInterface';
import { appliquerMetadonnees } from './metadonneesDocument';

export const appliquerLangueAuDocument = (code) => {
    if (typeof document === 'undefined' || !document.documentElement) return;

    const racine = document.documentElement;
    racine.setAttribute('lang', code);
    racine.setAttribute('dir', directionDe(code));

    // Repère pour le CSS qui a vraiment besoin de connaître la langue, plutôt
    // que la direction (des règles typographiques : espace avant les deux-points
    // en français, chasse des chiffres en arabe).
    racine.setAttribute('data-langue', code);

    if (!racine.classList.contains('notranslate')) {
        racine.classList.add('notranslate');
    }

    appliquerManifeste(code);
    appliquerMetadonnees(code);
};

/**
 * Fait pointer `<link rel="manifest">` sur la version de la langue affichee.
 *
 * Le manifeste porte le NOM et la DESCRIPTION de l'application installee : ce
 * qui s'inscrit sous l'icone de l'ecran d'accueil. Un usager qui a mis
 * l'interface en allemand n'a pas de raison d'installer « Assistants
 * pedagogiques IA ».
 *
 * Le navigateur relit l'attribut quand il evalue l'installabilite, apres le
 * chargement : le changer ici suffit. iOS, lui, IGNORE le manifeste et lit
 * `apple-mobile-web-app-title` — on la met a jour aussi, mais elle ne porte
 * que « 1cours1bot », qui ne se traduit pas.
 *
 * Le francais garde `/manifest.json` : c'est le fichier de reference, celui
 * qui est servi si la langue n'a pas le sien.
 */
const appliquerManifeste = (code) => {
    const lien = document.querySelector('link[rel="manifest"]');
    if (!lien) return;
    const voulu = code === LANGUE_PAR_DEFAUT ? '/manifest.json' : `/manifest.${code}.json`;
    // On ne reecrit que si necessaire : reassigner `href` relance l'evaluation
    // d'installabilite du navigateur.
    if (!lien.getAttribute('href').endsWith(voulu)) lien.setAttribute('href', voulu);
};

/**
 * La locale à passer aux `toLocaleDateString` / `Intl.*`.
 *
 * Le dépôt en compte plus d'une centaine, aujourd'hui figés sur `'fr-FR'`. Ils
 * passeront tous par ici : c'est ce qui évite d'avoir à retrouver la langue
 * courante dans chaque composant qui affiche une date.
 */
export const localeCourante = (code) => localeIntlDe(code);
